<script lang="ts" setup>
import { type Editor, getMarkAttributes } from '@tiptap/vue-3'
import type { StyleValue } from 'vue'
import { computed } from 'vue'
import { DropdownMenuCheckboxItem, DropdownMenuSeparator } from '@/foundation/dropdown-menu'
import ActionDropdownButton from '@/notebook/components/ActionDropdownButton.vue'
import { useLocale } from '@/notebook/locales'
import type { ButtonViewReturnComponentProps } from '@/notebook/type'

export type Item = {
  title: string
  icon?: any
  level?: number
  isActive: NonNullable<ButtonViewReturnComponentProps['isActive']>
  action?: ButtonViewReturnComponentProps['action']
  style?: StyleValue
  shortcutKeys?: string[]
  disabled?: boolean
  divider?: boolean
  default?: boolean
}
type Props = {
  editor: Editor
  disabled?: boolean
  color?: string
  shortcutKeys?: string[]
  maxHeight?: string | number
  tooltip?: string
  items?: Item[]
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  color: undefined,
  maxHeight: undefined,
  tooltip: '',
  shortcutKeys: undefined,
  items: () => [],
})

const { t } = useLocale()
const fontFamilyOptions = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const fontFamilyOptions = props.editor.extensionManager.extensions.find((e) => e.name === 'fontFamily')!.options
  const fonts = {
    [t.value('editor.fontFamily.default')]: t.value('editor.fontFamily.default'),
    ...fontFamilyOptions.fontFamilyMap,
  }

  return fonts
})
const activeFontFamily = computed(() => {
  return getMarkAttributes(props.editor.state, 'textStyle').fontFamily || t.value('editor.fontFamily.default')
})

function toggleFontType(name: string) {
  if (name === activeFontFamily.value) {
    props.editor.commands.unsetFontFamily()
  } else {
    props.editor.commands.setFontFamily(name)
  }
}
</script>

<template>
  <ActionDropdownButton :disabled="disabled" :tooltip="tooltip" :title="activeFontFamily" btn_class="min-w-24 max-w-32">
    <template v-for="(item, index) in fontFamilyOptions" :key="index">
      <DropdownMenuCheckboxItem :checked="activeFontFamily === item" @click="toggleFontType(item)">
        <span class="ml-1 h-full" :data-font="item" :style="{ fontFamily: item ?? undefined }">
          {{ item }}
        </span>
      </DropdownMenuCheckboxItem>
      <DropdownMenuSeparator v-if="item === t('editor.fontFamily.default')" />
    </template>
  </ActionDropdownButton>
</template>
